/* eslint-disable camelcase */
import _ from 'lodash';
import Builder from 'react-native-jala-orm/src/model/Builder';
import moment from 'moment';
import Model from './Model';
import { translate } from "../i18n"
import { createAxios } from "../utils/axios"
import { Platform } from 'react-native';
import { Sentry } from "../components"

/**
 * @property {() => Builder|SyncableModel} canBePushed
 */
class SyncableModel extends Model {
    /**
     * Indicates if the IDs are auto-incrementing.
     *
     * @var {Boolean}
     */
    incrementing = false

    /** @var {Boolean} debug */
    static debug = false

    /** @var {Number|null} syncProgress */
    static syncProgress = null

    /** @var {Number|null} pushProgress */
    static pushProgress = null

    /** @var {Boolean} allowReplaceUpdate */
    static allowReplaceUpdate = false

    /** @var {Boolean} syncOnly */
    static syncOnly = false

    /** @var {Boolean} syncOnly */
    static forceSync = false

    /** @var {Boolean} hasSyncedAt */
    static hasSyncedAt = true

    /** @var {String} endpoint */
    static endpoint = ''

    /** @var {Array} _childs */
    static _childs = []

    /** @var {Array} _parents */
    static _parents = []

    /** @var {Array} _appends */
    _appends = [
      'is_can_be_pushed',
      'is_modified',
      'is_not_synced',
    ]

    /**
     * The name of the "synced at" column.
     *
     * @var {String}
     */
    static SYNCED_AT = 'synced_at';

    /**
     * The name of the "deleted at" column.
     *
     * @var {String}
     */
    static DELETED_AT = 'deleted_at';

    /**
     * SyncAll Promise
     *
     * @type {Promise<unknown>|null}
     */
    static syncAllPromise = null;

    /**
     * PushAll Promise
     *
     * @type {Promise<unknown>|null}
     */
    static pushAllPromise = null;

    static online = false;

    static createAxios = createAxios;

    static _boot() {
      super._boot();

      this.addGlobalScope('soft_delete', (query: Builder) => {
        if (this.hasSyncedAt)
          query.whereNull(query.getModel().getQualifiedDeletedAtColumn());
      });
    }

    getDates() {
      const defaults = [
        this.constructor.CREATED_AT,
        this.constructor.UPDATED_AT,
        this.constructor.SYNCED_AT,
        this.constructor.DELETED_AT
      ];

      return this.usesTimestamps()
        ? _.uniq([...this._dates, ...defaults])
        : _.uniq([...this._dates, ...[this.constructor.SYNCED_AT, this.constructor.DELETED_AT]]);
    }

    /**
     *
     * @param query
     */
    // eslint-disable-next-line no-unused-vars,class-methods-use-this
    scopeSyncUpdateAdditionalQuery(query: Builder) {
      //
    }

    /**
     * @param {Builder} query
     * @returns {*}
     */
    // eslint-disable-next-line class-methods-use-this
    scopeCanBePushed(query: Builder) {
      return query.where((q: Builder) => {
        q.where((qW: Builder) => {
          qW.modified();
        }).orWhere((qW: Builder) => {
          qW.notSynced();
        });
      });
    }

    /**
     *
     * @param {Builder} query
     * @returns {string|Builder|*}
     */
    // eslint-disable-next-line class-methods-use-this
    scopeModified(query: Builder) {
      return query.withoutGlobalScope('soft_delete').beenSynced().where((q: Builder) => {
        q.whereColumn(q.qualifyColumn('synced_at'), '<', q.qualifyColumn('updated_at'))
          .orWhereNotNull(q.qualifyColumn('deleted_at'));
      });
    }

    /**
     *
     * @param {Builder} query
     * @returns {string|Builder|*}
     */
    // eslint-disable-next-line class-methods-use-this
    scopeNotModified(query: Builder) {
      return query.beenSynced().whereColumn(query.qualifyColumn('synced_at'), '>=', query.qualifyColumn('updated_at'))
        .whereNull(query.qualifyColumn('deleted_at'));
    }

    /**
     * @param query
     * @returns {string|Builder}
     */
    // eslint-disable-next-line class-methods-use-this
    scopeNotSynced(query: Builder) {
      return query.whereNull(query.qualifyColumn('synced_at'));
    }

    // eslint-disable-next-line class-methods-use-this
    scopeBeenSynced(query: Builder) {
      return query.whereNotNull(query.qualifyColumn('synced_at'));
    }

    async save(options = {}) {
      if (this.constructor.online) return true;
      return super.save(options)
    }

    async delete() {
      if (this.constructor.online) return true;
      return super.delete()
    }

    /**
     * Sync All Populate
     *
     * @param {Object} type
     * @param {Object} response
     * @param {Array} sqlParams
     * @param {String} timestamp
     * @param {Function|undefined} onSyncProgressUpdate
     * @param {Function|undefined} onProcessed
     * @return {Promise}
     */
    static syncAllPopulate(type, response, sqlParams, timestamp, onSyncProgressUpdate = undefined, onProcessed = undefined) {
      if (this.debug) console.log(this.name, { response });
      const keyName = (new this()).getKeyName();
      const models = ((response.data || {}).data || []).map(
        (datum) => this.reformatResponse(datum, true)
      );
      const total = ((response.data || {}).meta || {}).total;
      const ids = models.map((datum) => datum[keyName]);
      const notModifiedIds = [];
      const createModels = [];
      const updateModels = [];
      const paramsCount = sqlParams.reduce(
        (r, sp) => r + (sp[sp.length - 1] instanceof Array
          ? sp[sp.length - 1].length : 1), 0
      );

      let totalProcessed = 0;
      if (onSyncProgressUpdate instanceof Function) {
        onSyncProgressUpdate(1, total, totalProcessed);
      }
      let promise = Promise.resolve({});
      _.chunk(ids, 999 - paramsCount).forEach((idsChunked) => {
        promise = promise.then(
          (results) => this.query().withoutGlobalScope('soft_delete')
            .addArrayOfWheres(sqlParams, 'and', 'whereIn')
            .whereIn((new this()).getQualifiedKeyName(), idsChunked)
            .get()
            .then((value) => {
              const res = results;
              value.forEach((val) => {
                res[val.getKey()] = val;
              });
              return res;
            })
        );
      });
      return promise
        .then((modelsObject) => {
          let promiseModelsObject = Promise.resolve(null);

          models.forEach((model) => {
            const notModifiedModel = modelsObject[model[keyName]];
            if (notModifiedModel) {
              if (notModifiedModel.isCanBePushed() && !this.syncOnly) {
                if (onProcessed instanceof Function) totalProcessed = onProcessed(1)
                else totalProcessed += 1;
                // totalProcessed += 1;
                this.syncProgress = (totalProcessed * 100) / total;
                if (onSyncProgressUpdate instanceof Function) {
                  onSyncProgressUpdate(this.syncProgress, total, totalProcessed);
                }
                if (this.debug) console.log(this.name, 'Skipped', this.syncProgress, totalProcessed, total);
                return;
              }
              const isModified = (model: any, originalModel: any) => Object.keys(model).reduce((r, key) => (
                r || (model[key] !== originalModel[key])
              ), false)
              if (
                (notModifiedModel.getOriginal('updated_at') === model.updated_at)
                || (!model.updated_at && !isModified(model, notModifiedModel.toArray()))
              ) {
                notModifiedIds.push(model[keyName]);
              } else {
                updateModels.push({
                  ..._.pick(model, (new this()).getFillable()),
                  ...(this.hasSyncedAt ? { synced_at: timestamp } : {})
                });
              }
            } else {
              createModels.push({
                ..._.pick(model, (new this()).getFillable()),
                ...(this.hasSyncedAt ? { synced_at: timestamp } : {})
              });
            }
          });

          if (!this.allowReplaceUpdate) {
            updateModels.forEach((model) => {
              promiseModelsObject = promiseModelsObject.then(() => this.query()
                .withoutGlobalScope('soft_delete')
                .syncUpdateAdditionalQuery()
                .addArrayOfWheres(sqlParams, 'and', 'whereIn')
                .where((new this()).getQualifiedKeyName(), model[keyName])
                .getQuery()
                .update(model))
                .then(() => {
                  if (onProcessed instanceof Function) totalProcessed = onProcessed(1)
                  else totalProcessed += 1;
                  this.syncProgress = (totalProcessed * 100) / total;
                  if (onSyncProgressUpdate instanceof Function) {
                    onSyncProgressUpdate(this.syncProgress, total, totalProcessed);
                  }
                  if (this.debug) console.log(this.name, 'Update', this.syncProgress, totalProcessed, total);
                });
            });
          } else {
            _.chunk(updateModels, Math.floor(998 / (new this()).getFillable().length))
              .forEach((updateModelsChunked) => {
                promiseModelsObject = promiseModelsObject
                  .then(() => this.query()
                    .withoutGlobalScope('soft_delete')
                    .syncUpdateAdditionalQuery()
                    .whereIn(
                      this.query().getModel().getQualifiedKeyName(),
                      updateModelsChunked.map((model) => model[keyName])
                    )
                    .getQuery()
                    .delete())
                  .then(() => this.query()
                    .withoutGlobalScope('soft_delete')
                    .getQuery()
                    .insertOrIgnore(updateModelsChunked))
                  .then(() => {
                    if (onProcessed instanceof Function) totalProcessed = onProcessed(updateModelsChunked.length)
                    else totalProcessed += updateModelsChunked.length;
                    this.syncProgress = (totalProcessed * 100) / total;
                    if (onSyncProgressUpdate instanceof Function) {
                      onSyncProgressUpdate(this.syncProgress, total, totalProcessed);
                    }
                    if (this.debug) {
                      console.log(
                        this.name, 'Delete Insert', this.syncProgress, totalProcessed, total
                      );
                    }
                  });
              });
          }

          _.chunk(createModels, Math.floor(998 / (new this()).getFillable().length))
            .forEach((createModelsChunked) => {
              promiseModelsObject = promiseModelsObject.then(() => this.query()
                .withoutGlobalScope('soft_delete')
                .getQuery()
                .insertOrIgnore(createModelsChunked))
                .then(() => {
                  if (onProcessed instanceof Function) totalProcessed = onProcessed(createModelsChunked.length)
                  else totalProcessed += createModelsChunked.length;
                  this.syncProgress = (totalProcessed * 100) / total;
                  if (onSyncProgressUpdate instanceof Function) {
                    onSyncProgressUpdate(this.syncProgress, total, totalProcessed);
                  }
                  if (this.debug) console.log(this.name, 'Insert', this.syncProgress, totalProcessed, total);
                });
            });

          if (notModifiedIds.length) {
            _.chunk(notModifiedIds, 998 - paramsCount)
              .forEach((notModifiedIdsChunked) => {
                promiseModelsObject = promiseModelsObject.then(
                  async () => this.hasSyncedAt && this.query()
                    .withoutGlobalScope('soft_delete')
                    .syncUpdateAdditionalQuery()
                    .addArrayOfWheres(sqlParams, 'and', 'whereIn')
                    .whereIn((new this()).getQualifiedKeyName(), notModifiedIdsChunked)
                    .getQuery()
                    .update({ synced_at: timestamp })
                ).then(() => {
                  if (onProcessed instanceof Function) totalProcessed = onProcessed(notModifiedIdsChunked.length)
                  else totalProcessed += notModifiedIdsChunked.length;
                  this.syncProgress = (totalProcessed * 100) / total;
                  if (onSyncProgressUpdate instanceof Function) {
                    onSyncProgressUpdate(this.syncProgress, total, totalProcessed);
                  }
                  if (this.debug) {
                    console.log(
                      this.name, 'Not modified', this.syncProgress, totalProcessed, total
                    );
                  }
                });
              });
          }

          if (type.type === 2) {
            promiseModelsObject = promiseModelsObject.then(
              () => this.query()
                .withoutGlobalScope('soft_delete')
                .syncUpdateAdditionalQuery()
                .modified()
                .addArrayOfWheres(sqlParams, 'and', 'whereIn')
                .getQuery()
                .update({
                  updated_at: moment.utc(timestamp).add(1, 'second').format('YYYY-MM-DD HH:mm:ss'),
                  synced_at: timestamp
                })
            ).then(
              async () => this.hasSyncedAt && this.query()
                .withoutGlobalScope('soft_delete')
                .syncUpdateAdditionalQuery()
                .notModified()
                .addArrayOfWheres(sqlParams, 'and', 'whereIn')
                .getQuery()
                .update({ synced_at: timestamp })
            );
          }

          return promiseModelsObject;
        });
    }

    /**
     * Sync All Clean
     *
     * @param {Array} sqlParams
     * @param {String} timestamp
     * @return {Promise}
     */
    static syncAllClean(sqlParams, timestamp) {
      return this.query()
        .withoutGlobalScope('soft_delete')
        .syncUpdateAdditionalQuery()
        .notModified()
        .addArrayOfWheres(sqlParams, 'and', 'whereIn')
        .where((new this()).qualifyColumn('synced_at'), '<>', timestamp)
        .getQuery()
        .delete()
        .then(() => true)
    }

    /**
     * Sync All
     *
     * @param {Array|Object} params
     * @param {Array} sqlParams
     * @param {Boolean} cleanAfterSync
     * @param {Function} onSyncProgressUpdate
     * @param {Function} onError
     * @return {Promise}
     */
    static syncAll(
      params = {}, sqlParams = [], cleanAfterSync = true,
      onSyncProgressUpdate = undefined, onError = undefined, interceptResponse = undefined
    ) {
      if (Platform.OS === 'web' || this.online) return Promise.resolve(true);
      if (this.syncAllPromise === null) this.syncAllPromise = Promise.resolve(null);

      let timestamp = moment().utc().format('YYYY-MM-DD HH:mm:ss');
      let page = 1;
      const perPage = params.per_page || 998;
      let count = 0;
      let totalProcessed = 0;
      let total = 0;

      const check = () => Promise.all([
        this.query().withoutGlobalScope('soft_delete').beenSynced()
          .addArrayOfWheres(sqlParams, 'and', 'whereIn')
          .orderBy((new this()).qualifyColumn('synced_at'))
          .first(),
        this.query().withoutGlobalScope('soft_delete').beenSynced()
          .addArrayOfWheres(sqlParams, 'and', 'whereIn')
          .count(),
        this.query().withoutGlobalScope('soft_delete').canBePushed()
          .addArrayOfWheres(sqlParams, 'and', 'whereIn')
          .count(),
      ]).then(([
        modelOldestSyncedAt, totalSynced, totalCanBePushed
      ]) => (this.createAxios().get(this.endpoint, {
        params: {
          sort: 'updated_at,desc', ...params, per_page: 1, simple: undefined
        }
      }).then((response) => {
        const { data, meta } = response.data;
        if (this.debug) console.log(this.name, { response, meta, totalSynced });
        if (response.headers.date && moment().parseZone(response.headers.date).isValid()) {
          timestamp = moment().parseZone(response.headers.date).utc().format('YYYY-MM-DD HH:mm:ss');
        } else timestamp = moment().utc().format('YYYY-MM-DD HH:mm:ss');
        total = meta.total;
        this.syncProgress = 0;
        if (meta.total !== totalSynced || (totalCanBePushed && this.syncOnly)) {
          if (data.length && data[0].updated_at) {
            timestamp = moment.utc(data[0].updated_at).isAfter(moment.utc(timestamp))
              ? moment.utc(data[0].updated_at).format('YYYY-MM-DD HH:mm:ss') : timestamp;
          }
          return { type: 1 };
        }
        if (data.length && modelOldestSyncedAt) {
          timestamp = moment.utc(data[0].updated_at).format('YYYY-MM-DD HH:mm:ss');
          return (
            modelOldestSyncedAt.synced_at
            && moment.utc(data[0].updated_at).isAfter(modelOldestSyncedAt.synced_at)
          ) ? { type: 2, synced_at: modelOldestSyncedAt.synced_at.format('YYYY-MM-DD HH:mm:ss') } : { type: 0 };
        }
        return { type: 0 };
      })));

      /**
        *
        * @param {Object} type {type: 0}: No update, {type: 1}: All new, {type: 2}: Partial update
        * @returns {Promise<unknown>|Promise<null>}
        */
      const fetchModels = (type = { type: 0 }) => {
        if (!type.type) return Promise.resolve(true);
        if (onSyncProgressUpdate instanceof Function) {
          onSyncProgressUpdate(this.syncProgress, total, 0);
        }
        return this.createAxios().get(this.endpoint, {
          params: {
            page,
            per_page: perPage,
            sort: 'updated_at,desc',
            updated_at__gt: (type.type === 2)
              ? type.synced_at : undefined,
            updated_at__lte: (type.type === 2) ? timestamp : undefined,
            ...params,
          }
        }).then((response) => {
          count = response.data.data.length
          if (interceptResponse instanceof Function)
            interceptResponse(response)
          return this.syncAllPopulate(
            type, response, sqlParams, timestamp,
            onSyncProgressUpdate,
            (processedCount) => {
              totalProcessed += processedCount
              return totalProcessed;
            }
          )
        }).then(() => {
          if (count === perPage) {
            page += 1;
            return fetchModels(type);
          }

          return cleanAfterSync
            ? this.syncAllClean(sqlParams, timestamp)
            : Promise.resolve(true);
        });
      };

      this.syncAllPromise = this.syncAllPromise
        .then(() => (this.hasSyncedAt && Platform.OS !== 'web' && !this.forceSync ? check() : ({ type: 1 })))
        .then((type) => Platform.OS !== 'web' ? fetchModels(type) : true)
        .catch((error) => {
          if (error && !error.response && error instanceof Error && !error.message.includes('Request Timeout') && !error.message.includes('Network Error')) {
            Sentry.captureException(error);
          }
          console.warn(this.name, error);
          if (onError instanceof Function) onError(error);
          return false;
        })
        .then((success) => {
          if (success) this.syncProgress = 100;
          if (onSyncProgressUpdate instanceof Function) {
            onSyncProgressUpdate(this.syncProgress);
          }
          return success;
        });

      return this.syncAllPromise;
    }

    /**
     * Sync
     *
     * @returns {SyncableModel|null}
     */
    sync() {
      if (!this.exists) throw new Error("Model isn't exists");
      const timestamp = moment().utc().format('YYYY-MM-DD HH:mm:ss');

      const fetchModel = () => this.constructor.createAxios().get(`${this.constructor.endpoint}/${this.getKey()}`);

      return fetchModel()
        .then((r) => (async (response) => {
          const model = this.constructor.reformatResponse((response.data || {}).data || {});

          if (!_.isEmpty(this.getFillable())) this.fill(_.pick(model, this.getFillable()));
          else {
            _.forEach(model, (value, key) => {
              this[key] = value;
            });
          }
          this.updated_at = model.updated_at || timestamp;
          this.synced_at = timestamp;
          this.timestamps = false;
          await this.save();
          this.timestamps = true;
          return this;
        })(r))
        .catch((error) => {
          // console.warn(this.constructor.name, error);
          if (error.response && error.response.status === 404) {
            return this.forceDelete().then(() => this);
          }
          throw error;
        });
    }

    /**
     * Push All
     * @param {Array} sqlParams
     * @param {Boolean} checkParent
     * @param {Function} onPushProgressUpdate
     * @param {Function} onError
     * @param {Boolean} stopOnError
     */
    static pushAll(
      sqlParams = [],
      checkParent = true,
      onPushProgressUpdate = undefined,
      onError = undefined,
      stopOnError = false
    ) {
      if (this.pushAllPromise === null) this.pushAllPromise = Promise.resolve(null);

      return this.query().withoutGlobalScope('soft_delete')
        .addArrayOfWheres(sqlParams, 'and', 'whereIn')
        .canBePushed()
        .get()
        .then((models) => {
          const total = models.length;
          let totalProcessed = 0;
          let pushError = false;
          this.pushProgress = 0;
          if (onPushProgressUpdate instanceof Function) {
            onPushProgressUpdate(this.pushProgress, 0, 0);
          }
          models.forEach((model) => {
            this.pushAllPromise = this.pushAllPromise
              .then(
                () => (stopOnError && pushError ? Promise.resolve(null) : model.push(checkParent))
                  .catch((error) => {
                    if (onError instanceof Function) onError(error);
                    pushError = true;
                    return false;
                  })
                  .finally(() => {
                    totalProcessed += 1;
                    this.pushProgress = (totalProcessed * 100) / total;
                    if (onPushProgressUpdate instanceof Function) {
                      onPushProgressUpdate(this.pushProgress, total, totalProcessed);
                    }
                  })
              );
          });
          return this.pushAllPromise;
        }, (error) => {
          // console.warn(this.name, error);
          if (onError instanceof Function) onError(error);
          throw error;
        })
        .finally(() => {
          this.pushProgress = null;
          if (onPushProgressUpdate instanceof Function) {
            onPushProgressUpdate(this.pushProgress);
          }
        });
    }

    /**
     * @returns {SyncableModel|null}
     */
    push(checkParent = true) {
      let promise = Promise.resolve(null);
      if (!this.exists && !this.isNotSynced()) throw new Error("Model isn't exists");
      if (!this.exists) return promise.then(() => this);
      if (checkParent) {
        this.constructor._parents.forEach((parent) => {
          promise = promise.then(() => this[`relation${_.upperFirst(_.camelCase(parent))}`]().canBePushed().first()
            .then((model) => {
              if (model) return model.push();
              return model;
            }));
        });
      }
      if (this.constructor.syncOnly) {
        promise = promise.then(() => this.sync());
      } else if (this.isNotSynced()) {
        promise = promise.then(() => this.storeToServer());
      } else if (this.isModified()) {
        if (!this.deleted_at) promise = promise.then(() => this.updateToServer());
        else if (this.deleted_at) promise = promise.then(() => this.deleteFromServer());
      }
      return promise.then(() => this);
    }

    /**
     *
     * @returns {SyncableModel}
     * @private
     */
    storeToServer() {
      const timestamp = moment().utc().format('YYYY-MM-DD HH:mm:ss');

      const pushModel = () => this.constructor.createAxios().post(`${this.constructor.endpoint}`, _.pick(this.toArray(), this.getFillable()));

      return pushModel()
        .then((r) => (async (response) => {
          const model = this.constructor.reformatResponse((response.data || {}).data || {});
          const context = (await this.constructor.query()
            .withoutGlobalScopes().find(model[this.getKeyName()])) || this;
          if (context.getKey() !== this.getKey()) await this.forceDelete().catch();

          if (!_.isEmpty(context.getFillable())) context.fill(_.pick(model, context.getFillable()));
          else {
            _.forEach(model, (value, key) => {
              context[key] = value;
            });
          }
          context.updated_at = model.created_at || model.updated_at || timestamp;
          context.synced_at = model.created_at || model.updated_at || timestamp;
          context.timestamps = false;
          await context.save().finally(() => {
            context.timestamps = true;
          });
          return context;
        })(r))
        .catch((error) => {
          // console.warn(this.constructor.name, error);
          // if (error.response && error.response.status === 422) {
          //   return this.forceDelete().then(() => this);
          // }
          let { message } = error;
          if (error.response) {
            switch (error.response.status) {
              case 403:
                message = (error.response.data || {}).message || error.message;
                break;
              case 422:
                message = Object.keys(error.response.data.errors).map((k) => (
                  `${error.response.data.errors[k].join(', ')}`
                )).join(', ');
                break;
              default:
                message = error.response.message || error.message;
                break;
            }
          }
          if (error && !error.response && error instanceof Error && !error.message.includes('Request Timeout') && !error.message.includes('Network Error')) {
            Sentry.captureException(error);
          }
          if (message === 'Network Error') message = translate(message);
          throw new Error(`[${this.constructor.name}]:\n${message}`);
        });
    }

    /**
     *
     * @returns {SyncableModel|null}
     * @private
     */
    updateToServer() {
      const timestamp = moment().utc().format('YYYY-MM-DD HH:mm:ss');
      const pushModel = () => this.constructor.createAxios().put(`${this.constructor.endpoint}/${this.getKey()}`, _.pick(this.toArray(), this.getFillable()));

      return pushModel()
        .then((r) => (async (response) => {
          const model = this.constructor.reformatResponse((response.data || {}).data || {});
          const context = (await this.constructor.query()
            .withoutGlobalScopes().find(model[this.getKeyName()])) || this;

          if (!_.isEmpty(context.getFillable())) context.fill(_.pick(model, context.getFillable()));
          else {
            _.forEach(model, (value, key) => {
              context[key] = value;
            });
          }
          context.updated_at = model.updated_at || timestamp;
          context.synced_at = model.updated_at || timestamp;
          context.timestamps = false;
          await context.save().finally(() => {
            context.timestamps = true;
          });
          return context;
        })(r))
        .catch((error) => {
          // console.warn(this.constructor.name, error);
          // if (error.response && error.response.status === 404) {
          //   return this.forceDelete().then(() => this);
          // }
          // if (error.response && error.response.status === 422) {
          //   return this.sync();
          // }
          let { message } = error;
          if (error.response) {
            switch (error.response.status) {
              case 403:
                message = (error.response.data || {}).message || error.message;
                break;
              case 422:
                message = Object.keys(error.response.data.errors).map((k) => (
                  `${error.response.data.errors[k].join(', ')}`
                )).join(', ');
                break;
              default:
                message = error.response.message || error.message;
                break;
            }
          }
          if (error && !error.response && error instanceof Error && !error.message.includes('Request Timeout') && !error.message.includes('Network Error')) {
            Sentry.captureException(error);
          }
          if (message === 'Network Error') message = translate(message);
          throw new Error(`[${this.constructor.name}]:\n${message}`);
        });
    }

    /**
     *
     * @returns {Boolean}
     */
    deleteFromServer() {
      if (!this.exists) return true;

      const pushModel = () => this.constructor.createAxios().delete(`${this.constructor.endpoint}/${this.getKey()}`)
        .catch((error) => {
          if (error.response && error.response.status === 404) return error.response;
          throw error;
        });

      return pushModel()
        .then((r) => (async () => {
          await this.forceDelete();
          return this;
        })(r))
        .catch((error) => {
          // console.warn(this.constructor.name, error);
          let { message } = error;
          if (error.response) {
            switch (error.response.status) {
              case 403:
                message = (error.response.data || {}).message || error.message;
                break;
              case 404:
                return this.forceDelete().then(() => this);
              default:
                break;
            }
          }
          if (error && !error.response && error instanceof Error && !error.message.includes('Request Timeout') && !error.message.includes('Network Error')) {
            Sentry.captureException(error);
          }
          if (message === 'Network Error') message = translate(message);
          throw new Error(`[${this.constructor.name}]:\n${message}`);
        });
    }

    /**
     *
     * @returns {Boolean}
     */
    isCanBePushed() {
      return this.isModified() || this.isNotSynced();
    }

    getIsCanBePushedAttribute() {
      return this.isCanBePushed();
    }

    setIsCanBePushedAttribute(value = null) {
      return this
    }

    /**
     *
     * @returns {Boolean}
     */
    isModified() {
      return (!this.isNotSynced() && ((this.updated_at > this.synced_at) || !!this.deleted_at));
    }

    getIsModifiedAttribute() {
      return this.isModified();
    }

    setIsModifiedAttribute(value = null) {
      return this
    }

    /**
     *
     * @returns {Boolean}
     */
    isNotSynced() {
      return !this.synced_at;
    }

    getIsNotSyncedAttribute() {
      return this.isNotSynced()
    }

    setIsNotSyncedAttribute(value = null) {
      return this
    }

    /**
     * Perform a model insert operation.
     *
     * @return {Boolean}
     * @param {Builder} query
     */
    async _performInsert(query): boolean {
      if (!this.getIncrementing() && this._keyType === 'int' && !this[this.getKeyName()]) {
        this[this.getKeyName()] = -(moment().valueOf());
      }
      return super._performInsert(query);
    }

    /**
     * Insert the given attributes and set the ID on the model.
     *
     * @return void
     * @param {Builder} query
     * @param {Object} attributes
     */
    async _insertAndSetId(query, attributes) {
      await super._insertAndSetId(query, {
        [this.getKeyName()]: -(moment().valueOf()),
        ...attributes
      });
    }

    /**
     * Force a hard delete on a soft deleted model.
     *
     * @return {Boolean|null}
     */
    forceDelete() {
      this._forceDeleting = true;

      return _.tap(this.delete(), (deleted) => {
        this._forceDeleting = false;

        if (deleted) {
          this._fireModelEvent('forceDeleted', false);
        }
      });
    }

    /**
     * Perform the actual delete query on this model instance.
     *
     * @return {*}
     */
    _performDeleteOnModel() {
      if (this._forceDeleting || this.isNotSynced()) {
        this.exists = false;

        return this._setKeysForSaveQuery(this.newModelQuery()).forceDelete();
      }

      return this._runSoftDelete();
    }

    /**
     * Perform the actual delete query on this model instance.
     *
     * @return
     */
    async _runSoftDelete() {
      const query = this._setKeysForSaveQuery(this.newModelQuery());

      const time = this.freshTimestamp();

      const columns = { [this.getDeletedAtColumn()]: this.fromDateTime(time) };

      this[this.getDeletedAtColumn()] = time;

      if (this.timestamps && !(this.getUpdatedAtColumn() == null)) {
        this[this.getUpdatedAtColumn()] = time;

        columns[this.getUpdatedAtColumn()] = this.fromDateTime(time);
      }

      await query.update(columns);
    }

    /**
     * Restore a soft-deleted model instance.
     *
     * @return {Boolean|null}
     */
    restore() {
      // If the restoring event does not return false, we will proceed with this
      // restore operation. Otherwise, we bail out so the developer will stop
      // the restore totally. We will clear the deleted timestamp and save.
      if (this._fireModelEvent('restoring') === false) {
        return false;
      }

      this[this.getDeletedAtColumn()] = null;

      // Once we have saved the model, we will fire the "restored" event so this
      // developer will do anything they need to after a restore operation is
      // totally finished. Then we will return the result of the save call.
      this.exists = true;

      const result = this.save();

      this._fireModelEvent('restored', false);

      return result;
    }

    /**
     * Determine if the model instance has been soft-deleted.
     *
     * @return bool
     */
    trashed() {
      return !(this[this.getDeletedAtColumn()] == null);
    }

    /**
     * Register a restoring model event with the dispatcher.
     *
     * @param  {Function|String}  callback
     * @return void
     */
    static restoring(callback) {
      this._registerModelEvent('restoring', callback);
    }

    /**
     * Determine if the model is currently force deleting.
     *
     * @return {Boolean}
     */
    isForceDeleting() {
      return this._forceDeleting;
    }

    /**
     * Get the name of the "deleted at" column.
     *
     * @return string
     */
    getDeletedAtColumn() {
      return this.constructor.DELETED_AT ? this.constructor.DELETED_AT : 'deleted_at';
    }

    /**
     * Get the fully qualified "deleted at" column.
     *
     * @return string
     */
    getQualifiedDeletedAtColumn() {
      return this.qualifyColumn(this.getDeletedAtColumn());
    }

    static reformatResponse(response, raw = false) {
      const attributes = {
        ...(new this()).fill(response)[raw ? 'getAttributes' : 'toArray'](),
      }
      return _.omit(attributes, ['synced_at', 'deleted_at']);
    }

    static fetch(params = {}) {
      if (Platform.OS === 'web' || this.online)
        return this.createAxios().get(this.endpoint, {
          params: {
            per_page: 1000,
            ...params
          }
        }).then(({ data: { data } }) => {
          const synced_at = moment.utc().format('YYYY-MM-DD HH:mm:ss')
          return data.map((datum) => this.hydrate({...datum, synced_at}))
        })
      else {
        return super.fetch(params)
      }
    }
}

/** @var {Boolean} _forceDeleting */
SyncableModel.prototype._forceDeleting = false;

export default SyncableModel;
